import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormHelperText,
  Modal,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ASSESSMENT_LABELS,
  DEFAULT_ASSESSMENT_ID,
  GroupTrial,
  normalizeQuotaEntries,
  parseAssessmentQuotas,
  QuotaRow,
  validateGroupTrialForm
} from 'src/models/groupTrial';
import {
  ADMIN_RETRIEVE_GROUP_TRIALS,
  CREATE_GROUP_TRIAL,
  UPDATE_GROUP_TRIAL
} from './operations';

// Local copy of the shared modal styling. Defined here (rather than imported
// from the users page module) so the component graph stays free of the app's
// server/fetch-polyfill imports, keeping it renderable under jest.
const modalStyles = {
  position: 'absolute',
  top: '10%',
  left: '10%',
  right: '10%',
  bottom: '10%',
  overflow: 'scroll',
  display: 'block',
  '& .MuiPaper-root': {
    outline: 'none'
  }
} as const;

type Props = {
  open: boolean;
  organizationId: string;
  // Present when editing an existing trial; absent when creating.
  existingTrial?: GroupTrial;
  onClose: () => void;
  onSaved: () => void;
};

// Assessment ids offered as quota rows in the form. One entry today; the list
// shape lets more assessment ids be added without restructuring the UI.
const QUOTA_ASSESSMENT_IDS = [DEFAULT_ASSESSMENT_ID];

// Build the initial quota rows for the form. A row per known assessment id is
// always shown; when editing, each is pre-filled from the trial's parsed quotas.
const initialQuotaRows = (existingTrial?: GroupTrial): QuotaRow[] => {
  const existing = parseAssessmentQuotas(existingTrial?.assessmentQuotas);
  return QUOTA_ASSESSMENT_IDS.map((assessmentId) => {
    const match = existing.find((e) => e.assessmentId === assessmentId);
    return {
      assessmentId,
      quota: match ? String(match.quota) : ''
    };
  });
};

const GroupTrialFormModal = ({
  open,
  organizationId,
  existingTrial,
  onClose,
  onSaved
}: Props) => {
  const isEditing = !!existingTrial;

  const [name, setName] = useState<string>(existingTrial?.name ?? '');
  const [description, setDescription] = useState<string>(
    existingTrial?.description ?? ''
  );
  const [maxParticipants, setMaxParticipants] = useState<string>(
    existingTrial?.maxParticipants != null
      ? String(existingTrial.maxParticipants)
      : ''
  );
  const [quotaRows, setQuotaRows] = useState<QuotaRow[]>(
    initialQuotaRows(existingTrial)
  );
  const [validationError, setValidationError] = useState<string | undefined>();

  const mutation = isEditing ? UPDATE_GROUP_TRIAL : CREATE_GROUP_TRIAL;
  const [executeSave, { loading, error }] = useMutation(mutation, {
    errorPolicy: 'all',
    refetchQueries: [
      { query: ADMIN_RETRIEVE_GROUP_TRIALS, variables: { organizationId } }
    ]
  });

  const setQuotaValue = (assessmentId: string, quota: string) => {
    setQuotaRows((prev) =>
      prev.map((row) =>
        row.assessmentId === assessmentId ? { ...row, quota } : row
      )
    );
  };

  const handleSubmit = async () => {
    const message = validateGroupTrialForm({
      name,
      description,
      maxParticipants,
      quotas: quotaRows
    });
    setValidationError(message);
    if (message) {
      return;
    }

    // Both create and update send the full field set; `quotas` fully replaces
    // the stored per-participant map (blank rows are dropped).
    const common = {
      name: name.trim(),
      description: description.trim(),
      maxParticipants: Number(maxParticipants),
      quotas: normalizeQuotaEntries(quotaRows)
    };
    const input = isEditing
      ? { id: existingTrial!.id, ...common }
      : { organizationId, ...common };

    const result = await executeSave({ variables: { input } });

    // errorPolicy 'all' resolves (does not throw) on GraphQL errors; only
    // advance when the mutation actually returned data.
    const saved = isEditing
      ? result.data?.UpdateGroupTrial
      : result.data?.CreateGroupTrial;
    if (saved) {
      onSaved();
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={modalStyles}>
      <Card
        sx={{ maxWidth: 560, mx: 'auto', maxHeight: '100%', overflow: 'auto' }}
      >
        <CardContent>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {isEditing ? 'Edit Trial Group' : 'Create Trial Group'}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={3}>
            <TextField
              label="Trial name"
              size="small"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              label="Max participants"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Per-participant assessment quotas
              </Typography>
              <Stack spacing={2}>
                {quotaRows.map((row) => (
                  <TextField
                    key={row.assessmentId}
                    label={ASSESSMENT_LABELS[row.assessmentId] ?? row.assessmentId}
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Leave blank for no quota on this assessment."
                    value={row.quota}
                    onChange={(e) => setQuotaValue(row.assessmentId, e.target.value)}
                  />
                ))}
              </Stack>
            </Box>

            {(validationError || error) && (
              <FormHelperText error>
                {validationError ?? error?.message}
              </FormHelperText>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading
                  ? isEditing
                    ? 'Saving…'
                    : 'Creating…'
                  : isEditing
                    ? 'Save changes'
                    : 'Create trial group'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default GroupTrialFormModal;
